import { safeStorage } from "@utils/safeStorage"
import { proxy } from "valtio"
import { FerveAPI } from "./ferve-api-client"

export type WishlistNotification = {
  type: "added" | "removed"
}

export type PurchasedTicket = {
  sessionHash: string
  dateTime: string
  itemTitle: string
  venueName: string
  priceType: string
  cancelled: boolean
  finished: boolean
  print: boolean
  barcodes: {
    seat?: string
    scanned: number
    exchange: number
    hash: string
    number: string
  }[]
}

export class FerveWishlist {
  public state = proxy({
    hashes: new Set<string>(),
    purchased: [] as PurchasedTicket[],
  })

  public notifications = proxy({
    items: {} as Record<string, WishlistNotification>,
  })

  constructor(public api: FerveAPI) {
    if (env.client) {
      this.refresh()

      // Subscribe to storage changes
      this.api.onDestroy(safeStorage.subscribe(["last_wishlist_hashes"], () => this.updateFromStorage()))
    }
  }

  private setHashes(hashes: string[]) {
    this.state.hashes = new Set(hashes)
    safeStorage.setItem("last_wishlist_hashes", JSON.stringify({ hashes }))
  }

  private setPurchased(tickets: PurchasedTicket[]) {
    this.state.purchased = tickets
    safeStorage.setData("last_wishlist_hashes", tickets)
  }

  public updateFromStorage() {
    try {
      const hashes = safeStorage.getItem("last_wishlist_hashes")
      if (!hashes) return
      const data = JSON.parse(hashes)
      if (data.hashes) {
        this.state.hashes = new Set(data.hashes)
      }
    } catch (e) {}
  }

  private handleUpdateResult(data: any) {
    if (data && data.Success && data.SuccessMessages && data.SuccessMessages.length) {
      const wishlistItems = data.SuccessMessages[0].split(",")
      this.setHashes(wishlistItems.filter(Boolean))
    }
  }

  async add(itemHash: string | number) {
    this.state.hashes.add(String(itemHash))
    this.notify(itemHash, "added")
    const result = await this.api.GET("Wishlist/Add", { itemID: itemHash, location: "Web" })
    this.handleUpdateResult(result)
  }

  async remove(itemHash: string | number) {
    this.state.hashes.delete(String(itemHash))
    this.notify(itemHash, "removed")
    const result = await this.api.GET("Wishlist/Remove", { itemID: itemHash, location: "Web" })
    this.handleUpdateResult(result)
  }

  async refreshWishlist() {
    const result = await this.api.GET("Items/Schedule")
    if (Array.isArray(result)) {
      const wishlistItems = result.filter((item: any) => item.Wishlist).map((item: any) => String(item.Id))
      this.setHashes(wishlistItems.filter(Boolean))
    }
  }

  async refreshPurchased() {
    if (this.api.session?.loggedIn) {
      const result = await this.api.GET("Visitors/Tickets")
      if (Array.isArray(result)) {
        this.setPurchased(
          result
            .map((ticket: any) => ({
              sessionHash: String(ticket.ItemHash),
              dateTime: ticket.DateTime,
              itemTitle: ticket.ItemName,
              venueName: ticket.VenueName,
              priceType: ticket.Type,
              cancelled: ticket.Cancelled,
              finished: ticket.Finished,
              print: ticket.Print,
              barcodes: ticket.Barcodes.map((barcode: any) => ({
                exchange: barcode.Exchange,
                seat: barcode.Seat,
                scanned: barcode.Scanned,
                hash: barcode.Hash,
                number: barcode.Number,
              })),
            }))
            .filter((ticket: PurchasedTicket) => ticket.barcodes.some((barcode) => barcode.scanned < 3)),
        )
        return
      }
    }
    this.setPurchased([])
  }

  async refresh() {
    await this.refreshWishlist()
    this.refreshPurchased()
  }

  private notify(item: string | number, action: "added" | "removed") {
    this.notifications.items[String(item)] = { type: action }
  }
}
